import React, { FunctionComponent, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { getTitleByMeasurement } from "../../../../../../utils/NamesUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faExclamationTriangle, faPen, faTimes, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import {
  LoggedProgress,
  StudentGoalProgress,
} from "../../../../../../store/onboarding/meeting-module/types";
import {
  InterventionGroup,
  StudentGoal,
} from "../../../../../../store/onboarding/cases/types";
import DatePicker from "react-datepicker";
import CustomDateBoxInput from "../common/CustomDateBoxInput";
import { faSave } from "@fortawesome/free-regular-svg-icons";
import GoalInputGroup from "../goal-progress/GoalInputGroup";
import { connect, useSelector } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { updateProgressTowardsGoal } from "../../../../../../store/onboarding/meeting-module/actions";
import { OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { toastr } from "react-redux-toastr";
import { dateToFormatStringISO8601 } from "../../../../../utils/DateTimeUtils";
import { showConfirmDialog } from "../../../../../../store/confirm-dialog/actions";
import { useDispatch } from "react-redux";
import { deleteLoggedProgress } from "../../../../../../store/onboarding/cases/actions";
import { ApplicationState } from "../../../../../../store";
import useInterventionGroupAccess from "../../../../../../utils/hooks/useInterventionGroupAccess";

type OwnProps = {
  hideActions?: boolean;
  lp: LoggedProgress;
  sgp: StudentGoalProgress;
  interventionGroup: InterventionGroup;
  isStudentDetail?: boolean;
  isGroupDetail?: boolean;
  innerIndex?: number;
  fromInterventionFidelityChart?: boolean;
  expanded?: boolean;
};

type DispatchProps = {
  updateProgressTowardsGoal: (
    loggedProgressId: number,
    loggedProgress: Partial<LoggedProgress>
  ) => any;
  deleteLoggedProgress?: any;
};

type Props = OwnProps & DispatchProps;

const LoggedProgressRow: FunctionComponent<Props> = ({
  hideActions,
  lp,
  sgp,
  interventionGroup,
  updateProgressTowardsGoal,
  deleteLoggedProgress,
  isStudentDetail,
  isGroupDetail,
  innerIndex,
  fromInterventionFidelityChart,
  expanded
}) => {
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [observedDate, setObservedDate] = useState<Date | undefined>(undefined);
  const [studentGoal, setStudentGoal] = useState<
    (StudentGoal & LoggedProgress) | undefined
  >(undefined);
  const [valueError, setValueError] = useState<string>("");
  const [dateError, setDateError] = useState<string>("");

  const dispatch = useDispatch();

  const { isIntGrpIDsWithReadAccess } = useInterventionGroupAccess(interventionGroup?.id);

  const handleShow = (id: number) => {
    dispatch(
      showConfirmDialog({
        onConfirm: () => {
          deleteLoggedProgress(id);
        },
        title: "Delete Goal Progress",
        text: (
          <>
            <p className="font-weight-bold">Are you sure you want to delete this goal progress log?</p>
            <p className="text-center">
              <FontAwesomeIcon 
                icon={faExclamationTriangle}
                style={{color:'red'}}
              /> {" "}
              <i>This action cannot be undone.</i>
            </p>
          </>
        ),
        confirmButtonText: "DELETE",
        centered: true,
      })
    );
  };

  useEffect(() => {
    if (editing) {
      setObservedDate(moment(lp.date).toDate());
      const goal = interventionGroup.student_goals?.find(
        (sg) => sg.id === sgp.student_goal_id
      );
      if (goal) {
        setStudentGoal({ ...goal, ...lp, goal_id: sgp.student_goal_id });
      }
    }
  }, [editing]);

  const showActions = useMemo(() => {
    return !hideActions 
        && !isStudentDetail 
        && !isGroupDetail 
        && !isIntGrpIDsWithReadAccess 
        && !fromInterventionFidelityChart
  }, [hideActions, isStudentDetail, isGroupDetail, isIntGrpIDsWithReadAccess, fromInterventionFidelityChart]);

  const handleGoalLogChange = (event: React.ChangeEvent<any>) => {
    const { value } = event.target as HTMLInputElement;
    setStudentGoal((s) => (s ? { ...s, value: value } : undefined));
  };

  const handleUpdateLoggedProgress = () => {
    let hasError = false;
    if (!observedDate) {
      setDateError("This field is required.");
      hasError = true;
    } else {
      setDateError("");
    }
    if (!studentGoal || studentGoal.value === undefined || studentGoal.value === "") {
      setValueError("This field is required.");
      hasError = true;
    } else {
      setValueError("");
    }
    if (hasError) return;
    setLoading(true);
    updateProgressTowardsGoal(studentGoal!.id, {
      value: studentGoal!.value,
      date: observedDate && dateToFormatStringISO8601(observedDate),
    }).then(
      () => {
        setEditing(false);
        setLoading(false);
      },
      (err: string) => {
        toastr.error("Failed to update the progress", err);
        setLoading(false);
      }
    );
  };

  return editing ? (
    <tr className="border-left-0 border-right-0">
      <td colSpan={3} className="text-nowrap">
        <div className="flex-wrap-container">
          <div style={{ minWidth: 180 }}>
            <DatePicker
              maxDate={new Date()}
              popperPlacement="left"
              customInput={<CustomDateBoxInput />}
              selected={observedDate}
              onChange={(date: Date) => {
                setObservedDate(date);
                setDateError("");
              }}
            />
            {dateError && (
              <div style={{ color: "red", fontStyle: "italic" }}>{dateError}</div>
            )}
          </div>
          {studentGoal && (
            <div style={{ minWidth: "8.125rem" }}>
              <GoalInputGroup
                measurement={studentGoal.measurement}
                value={studentGoal.value}
                onChange={e => {
                  handleGoalLogChange(e);
                  setValueError("");
                }}
                inputProps={valueError ? { style: { border: "1px solid red" } } : {}}
              />
              {valueError && (
                <div style={{ color: "red", fontStyle: "italic" }}>{valueError}</div>
              )}
            </div>
          )}
          <div className="d-flex justify-content-center align-items-center">
          <button
            className="whiteBtnSm"
            onClick={() => setEditing(false)}
          >
            Cancel
          </button>
          <button
            className="blueBtnSm ml-3 d-flex align-items-center"
            onClick={handleUpdateLoggedProgress}
          >
            Save
            {loading && (
              <Spinner animation="border" size="sm" className="ml-3" />
            )}
          </button>
          </div>
        </div>
      </td>
    </tr>
  ) : (
    <tr className="border-left-0 border-right-0">
      <td className={`text-left ${innerIndex == 0 ? 'border-0' : ''}`} style={{width: expanded ? "30%" : "55px"}}>{moment.utc(lp.date).format("MM/DD/YYYY")}</td>
      <td className={`text-center ${innerIndex == 0 ? 'border-0' : ''}`} style={{width: expanded ? "30%" : "55px"}}>{getTitleByMeasurement(lp.value, sgp.measurement)}</td>
      <td className={`${innerIndex == 0 ? 'border-0 ' : ''}`} style={{width: expanded ? "30%" : "55px"}}>
        { showActions && (
          <div className="entry-actions d-flex align-items-center">
            <OverlayTrigger
                trigger={["hover", "focus"]}
                placement="top"
                overlay={
                  <Tooltip className="customTooltip" id= "tooltip-top">
                    Edit
                  </Tooltip>
                }
              >
                  <FontAwesomeIcon
                    icon={faPen}
                    onClick={() => {
                      setValueError("");
                      setDateError("");
                      setEditing(true);
                    }}
                    className="pointer mr-2"
                  />
            </OverlayTrigger>
            <OverlayTrigger
              trigger={["hover", "focus"]}
              placement="top"
              overlay={
                <Tooltip className="customTooltip" id= "tooltip-top">
                  Delete
                </Tooltip>
              }
            >
                <FontAwesomeIcon
                  icon={faTrashAlt}
                  size="1x"
                  className="pointer"
                  style={{color:'red'}}
                  onClick={() => handleShow(lp?.id!)}
                />
            </OverlayTrigger>
          </div>
        )}
      </td>
    </tr>
  );
};

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps =>
  bindActionCreators(
    {
      updateProgressTowardsGoal: updateProgressTowardsGoal,
      deleteLoggedProgress: deleteLoggedProgress,
    },
    dispatch
  );

export default connect(null, mapDispatchToProps)(LoggedProgressRow);
